/**
 * useDomainEnrichment — run the AI domain-enrichment SSE pipeline for a known domain.
 *
 * This is the single shared consumer of the `/organizations/enrich-domain`
 * streaming endpoint (step=fetching → analyzing → logo → done). It wraps
 * `useProgressStream` so callers get a plain `enrich(domain)` call plus the
 * streamed progress message and the final `DomainEnrichmentResult`.
 *
 * Both `useCompanyLookup` (create-time "look up a company") and the existing-org
 * "AI enrich" action build on this — the SSE consumption lives here once instead
 * of being duplicated per call site.
 */
import { useCallback } from 'react';
import { API_BASE_URL } from '../api/config';
import { useProgressStream } from './useProgressStream';
import type { DomainEnrichmentResult } from '../../modules/master_data/contacts/types';

const ENRICH_URL = `${API_BASE_URL}/api/v1/master-data/contacts/organizations/enrich-domain`;

export interface UseDomainEnrichmentReturn {
  /** Kick off enrichment for a resolved domain (SSE stream). */
  enrich: (domain: string) => Promise<void>;
  /** Final enriched fields, once the stream reaches step=done. */
  enrichment: DomainEnrichmentResult | null;
  /** Current step identifier (fetching / analyzing / logo / done / error). */
  currentStep: string | null;
  /** Human-readable progress message for the current step. */
  progressMessage: string | null;
  /** Error message if the stream failed. */
  error: string | null;
  /** Whether the stream is currently active. */
  isLoading: boolean;
  /** Reset state and abort any in-flight stream. */
  reset: () => void;
}

export function useDomainEnrichment(): UseDomainEnrichmentReturn {
  const stream = useProgressStream<DomainEnrichmentResult>();

  const enrich = useCallback(
    async (domain: string) => {
      await stream.trigger(ENRICH_URL, { domain });
    },
    [stream],
  );

  return {
    enrich,
    enrichment: stream.data,
    currentStep: stream.currentStep,
    progressMessage: stream.message,
    error: stream.error,
    isLoading: stream.isLoading,
    reset: stream.reset,
  };
}
